import React, { useMemo } from "react";
import { useImmerReducer } from "use-immer";
import { useFetchQlpFiles } from "../../Hooks/qlp_ddpcr/useFetchQlpFiles";
import { DataGrid, GridToolbar } from '@material-ui/data-grid';
import { Link } from "react-router-dom";
import Spinner from "../../common/Spinner";
import { formatDate } from "../../../utils/basic";
import { Dialog, DialogContent, DialogActions } from '@material-ui/core';
import CreateAutoDGPlate from "../../ddPcr/AutoDG/CreateAutoDGPlate";
import { usePlateWellActions } from "../../../store/plateWellStore";

const initialState = {
    rows: [],
    open: false,
    selectedRowData: null,
    searchQuery: "",
};

function reducer(draft, action) {

    const { type, payload } = action;
    switch (type) {

        case "SET_ROWS":
            draft.rows = payload;
            return draft;

        case "SET_OPEN":
            draft.open = payload;
            return draft;

        case "SET_SELECTED_ROW_DATA":
            draft.selectedRowData = payload;
            return draft;

        case "SET_SEARCH_QUERY":
            draft.searchQuery = payload;
            return draft;

        default: return draft;
    }
}

const QlpFilesList = () => {

    const filesListQuery = useFetchQlpFiles();
    const { setSelectedAnanlysisType, setTabValue } = usePlateWellActions();
    const [state, dispatch] = useImmerReducer(reducer, initialState);
    const gridData = filesListQuery?.isSuccess ? filesListQuery?.data : [];


    const handleSearchQueryChange = (event) => {
        dispatch({ type: "SET_SEARCH_QUERY", payload: event.target.value });
    }

    const handleCloseDialog = () => {
        dispatch({ type: "SET_OPEN", payload: false });
    };

    // useMemo to transform and filter grid data based on search query
    const filteredGridData = useMemo(() => {
        setSelectedAnanlysisType("");
        setTabValue("tab1");
        const formattedData = gridData?.map(item => {
            let parsedDate = new Date(item?.ingest_date);
            if (isNaN(parsedDate.getTime())) {
                console.warn("Invalid Date Detected:", item?.ingest_date);
                parsedDate = new Date(0); // Default to 1970-01-01
            }
            return {
                // data transformation logic here
                filename: item?.data_filename,
                fileinfo_datetime: item?.fileinfo_datetime,
                generalinfo_user: item?.generalinfo_user,
                ingest_date: formatDate(item?.ingest_date), // Format date
                ingest_date_sort: parsedDate?.getTime(), // Store raw Date object for sorting
                plate_uuid: item?.plate_uuid,
                project_name: item.project_name,
                run_uuid: item?.run_uuid,
                serialnumber: item?.serialnumber,
                action: /autodg|engineering/i.test(item?.data_filename) && item?.dg_meta === false ? "Add DGPlate" : "",
                file_ready: item?.file_ready,
                id: crypto.randomUUID(),// Generate a random ID for each row
            };

        });


        if (state?.searchQuery?.length >= 2) {
            return formattedData.filter(item =>
                item?.filename.toLowerCase().includes(state?.searchQuery.toLowerCase())
            );
        }
        return formattedData;
    }, [gridData, state?.searchQuery]);

    const columns = [
        {
            field: 'filename', headerName: 'Filename', width: 500,
            renderCell: (params) => (

                <Link to={`qlp_metrics/${params.row.plate_uuid}/${params.row.filename}`} className="hover:cursor-pointer hover:text-blue-600 hover:underline">
                    {params.value}
                </Link>

            )
        },
        {
            field: "action", headerName: "Action", width: 300,
            renderCell: (params) => (
                params.row.action === "Add DGPlate" ? (
                    <div className="w-full">
                        <button
                            className={`px-4 py-2 leading-6 text-center border rounded-md ${params.row.file_ready
                                ? "text-white bg-custom-green cursor-pointer"
                                : "text-gray-400 bg-gray-200 cursor-not-allowed"
                                }`}
                            type="button"
                            disabled={!params.row.file_ready}
                            onClick={() => {
                                if (params.row.file_ready) {
                                    dispatch({ type: 'SET_OPEN', payload: true });
                                    dispatch({ type: 'SET_SELECTED_ROW_DATA', payload: params.row });
                                }
                            }}
                        > Add DGPlate</button>
                    </div>
                ) : ''
            )
        },
        { field: 'serialnumber', headerName: 'Serial #', width: 200 },
        { field: 'generalinfo_user', headerName: 'User', width: 200 },
        {
            field: 'ingest_date', headerName: 'Date',
            sortable: true,
            //valueGetter: (params) => params.row.ingest_date_sort, // Use raw timestamp for sorting
            valueGetter: (params) => params.row.ingest_date, // Use formatted date for display
            sortComparator: (v1, v2) => {

                // Ensure both values exist and are numbers             
                // Convert formatted date back to timestamp for sorting
                const date1 = new Date(v1).getTime() || 0;
                const date2 = new Date(v2).getTime() || 0;

                return date1 - date2;
            },
            width: 200
        },
        { field: 'plate_uuid', headerName: 'UUID', width: 400 },
        { field: 'project_name', headerName: 'Project Name', width: 200 },
        {
            field: 'run_uuid', headerName: 'RUN_UUID', width: 400

        },


    ];

    return (
        <>

            <h5 className="text-custom-green font-bold text-center pb-10">QLP Files List</h5>
            <div className="oneToolContainer">
                <div className="pb-10">
                    <input
                        type="text"
                        value={state?.searchQuery}
                        placeholder="Search File Name..."
                        className="border border-gray rounded-[8px] px-2  py-1 text-sm w-40 focus:outline-none focus:ring-2 focus:ring-custom-green"
                        //onChange={(event) => setSearchQuery(event.target.value)}
                        onChange={handleSearchQueryChange}
                    />
                </div>

                {/* <div className="pt-10 grid" style={{ height: 800, width: '100%' }}> */}
                {filteredGridData?.length > 0 ?
                    <div className="flex h-[75dvh]">

                        <DataGrid className="hover:cursor-pointer"
                            // rows={state?.rows}
                            rows={filteredGridData}
                            columns={columns}
                            paginationMode="client"
                            sortingMode="client"
                            components={{
                                Toolbar: GridToolbar,
                            }}
                        />

                    </div> : <Spinner text="Fetching Data .. Please wait" />}
                <Dialog open={state?.open} onClose={handleCloseDialog}>

                    <DialogContent>
                        <CreateAutoDGPlate row={state?.selectedRowData} onClose={handleCloseDialog} />
                    </DialogContent>
                    <DialogActions>
                        <button className="text-black px-4 py-2 text-center  border border-gray-300 rounded-md"
                            onClick={() => dispatch({ type: 'SET_OPEN', payload: false })}>CANCEL</button>
                    </DialogActions>
                </Dialog>
            </div>
        </>
    );
};
export default QlpFilesList;